<script setup lang="ts">
  import { SelectInput, DatePicker, IconImage } from '@/shared/ui';
  import ApprovalRadioGroup from './ApprovalRadioGroup.vue';
  import RejectionJustificationSection from './RejectionJustificationSection.vue';
  import type { VerificationSaveData } from '../model';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import { useDateValidation } from '@/shared/lib';
  import { formatCivilDateToPtBR } from '@/shared/lib';

  import { ref, computed, watch } from 'vue';
  const props = withDefaults(
    defineProps<{
      onSave: (data: VerificationSaveData) => Promise<any>;
      documentRevisionId: string;
      positions: { id: string; name: string }[];
      approveOptionLabel?: string;
      rejectOptionLabel?: string;
      requiresRejectionJustification?: boolean;
      rejectPlaceholder?: string;
    }>(),
    {
      approveOptionLabel: 'Aprovar documento',
      rejectOptionLabel: 'Reprovar documento',
      requiresRejectionJustification: true,
      rejectPlaceholder: 'Explique porque o documento foi reprovado',
    }
  );

  const emit = defineEmits(['update:modelValue', 'error', 'saved']);

  const { selectedDate, isDatePickerOpen, validateAndShowError } = useDateValidation();

  const approvalStatus = ref<'' | 'approve' | 'reject'>('');
  const selectedRole = ref<string>('');
  const justification = ref<string>('');

  watch(approvalStatus, (newStatus) => {
    if (newStatus === 'approve') {
      justification.value = '';
    }
  });

  const isSaveButtonActive = computed(() => {
    if (!approvalStatus.value) {
      return false;
    }

    if (!selectedRole.value) {
      return false;
    }

    if (
      approvalStatus.value === 'reject' &&
      props.requiresRejectionJustification &&
      !justification.value?.trim()
    ) {
      return false;
    }

    return !(approvalStatus.value === 'approve' && !selectedDate.value);
  });

  const formattedDate = computed(() => {
    if (!selectedDate.value) return 'Selecione a data';
    return formatCivilDateToPtBR(selectedDate.value);
  });

  const submit = () => {
    const submitButton = document.getElementById('verification-form-submit') as HTMLFormElement;
    if (submitButton) {
      submitButton.click();
    }
  };

  defineExpose({
    submit,
    approvalStatus,
    selectedRole,
    justification,
    selectedDate,
    isSaveButtonActive,
  });

  const handleFormSubmit = async (event: Event) => {
    event.preventDefault();

    if (!approvalStatus.value) {
      emit('error', new Error('Por favor, selecione uma opção (Aprovar ou Reprovar)'));
      return;
    }

    if (!selectedRole.value) {
      emit('error', new Error('Por favor, selecione um cargo'));
      return;
    }

    if (approvalStatus.value === 'approve' && !selectedDate.value) {
      return;
    }

    if (approvalStatus.value === 'approve' && selectedDate.value) {
      if (!validateAndShowError(selectedDate.value)) {
        return;
      }
    }

    if (
      approvalStatus.value === 'reject' &&
      props.requiresRejectionJustification &&
      !justification.value.trim()
    ) {
      emit('error', new Error('Por favor, forneça uma justificativa para a reprovação'));
      return;
    }

    try {
      const formData: {
        approvalStatus: 'approve' | 'reject';
        selectedRole: string;
        justification: string;
        documentRevisionId: string;
        effectiveDate?: Date | null;
      } = {
        approvalStatus: approvalStatus.value,
        selectedRole: selectedRole.value,
        justification: justification.value,
        documentRevisionId: props.documentRevisionId,
      };

      if (approvalStatus.value === 'approve') {
        formData.effectiveDate = selectedDate.value;
      }

      await props.onSave(formData);
      emit('saved');
    } catch (error: any) {
      emit('error', error.message || 'Erro ao salvar verificação');
    }
  };
</script>

<template>
  <form
    class="divide-y divide-gray-02"
    @submit="handleFormSubmit"
  >
    <ApprovalRadioGroup
      v-model="approvalStatus"
      :approve-option-label="approveOptionLabel"
      :reject-option-label="rejectOptionLabel"
    />

    <div
      v-if="approvalStatus"
      class="p-6"
    >
      <div class="text-gray-07 text-sm/5 w-full text-left font-semibold mb-2">
        Cargo relacionado à Etapa de Verificação e Vigência:
      </div>
      <div class="max-w-md">
        <SelectInput
          v-model="selectedRole"
          :options="positions"
          label="Selecione seu cargo"
          placeholder="Selecione um cargo"
        />
      </div>
    </div>

    <div
      v-if="approvalStatus === 'approve'"
      class="p-6"
    >
      <div class="text-gray-07 text-sm/5 w-full text-left font-semibold mb-2">
        Data de vigência:
      </div>
      <div class="max-w-md">
        <button
          type="button"
          class="flex items-center gap-2 bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-07 focus:outline-none focus:border-dark-red font-medium"
          @click="isDatePickerOpen = true"
        >
          <IconImage
            :src="CalendarIcon"
            alt="Calendário"
            class="h-4 w-4"
          />
          {{ formattedDate }}
        </button>
        <DatePicker
          v-model:selected-date="selectedDate"
          v-model:is-open="isDatePickerOpen"
          :disable-past-dates="true"
          @apply="isDatePickerOpen = false"
          @cancel="isDatePickerOpen = false"
        />
      </div>
    </div>

    <RejectionJustificationSection
      v-if="approvalStatus === 'reject' && requiresRejectionJustification"
      v-model="justification"
      :placeholder="rejectPlaceholder"
    />
    <button
      id="verification-form-submit"
      type="submit"
      class="hidden"
    >
      Salvar
    </button>
  </form>
</template>
